
import { Heart, Leaf, Award } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";

export default function MissionValues() {
  return (
    <section className="py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-indigo-200/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-br from-purple-200/20 to-blue-200/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            What Drives Us Forward
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Our commitment to fresh, healthy nutrition is built on core values that guide every decision we make.
          </p>
        </div>

        {/* Values Cards Carousel */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card className="group hover:shadow-2xl transition-all duration-500 border-blue-200 hover:border-blue-300 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="p-4 bg-blue-100 rounded-2xl inline-flex border border-blue-200 group-hover:bg-blue-200 transition-colors">
                  <Heart className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center group-hover:text-blue-600 transition-colors">
                Health First
              </h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Every ingredient is carefully selected for maximum nutritional value. We believe healthy eating should be delicious, convenient, and accessible to everyone.
              </p>
              <div className="mt-6 flex justify-center">
                <Badge className="bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200 transition-colors">
                  Nutrition Focused
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-2xl transition-all duration-500 border-indigo-200 hover:border-indigo-300 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="p-4 bg-indigo-100 rounded-2xl inline-flex border border-indigo-200 group-hover:bg-indigo-200 transition-colors">
                  <Leaf className="h-8 w-8 text-indigo-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center group-hover:text-indigo-600 transition-colors">
                Sustainability
              </h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We partner with local organic farms and use eco-friendly packaging. Our carbon-neutral delivery ensures we protect the planet for future generations.
              </p>
              <div className="mt-6 flex justify-center">
                <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200 transition-colors">
                  100% Organic
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-2xl transition-all duration-500 border-purple-200 hover:border-purple-300 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="p-4 bg-purple-100 rounded-2xl inline-flex border border-purple-200 group-hover:bg-purple-200 transition-colors">
                  <Award className="h-8 w-8 text-purple-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center group-hover:text-purple-600 transition-colors">
                Quality Excellence
              </h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Every salad is prepared fresh daily by our expert chefs. We maintain the highest standards in food safety, preparation, and presentation.
              </p>
              <div className="mt-6 flex justify-center">
                <Badge className="bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200 transition-colors">
                  Premium Quality
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Impact Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-blue-200 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
            <div className="text-3xl font-bold text-blue-600 mb-2">50+</div>
            <div className="text-blue-700 text-sm font-medium">Local Farm Partners</div>
          </div>
          <div className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-indigo-200 hover:border-indigo-300 transition-all duration-300 hover:shadow-lg">
            <div className="text-3xl font-bold text-indigo-600 mb-2">100%</div>
            <div className="text-indigo-700 text-sm font-medium">Organic Ingredients</div>
          </div>
          <div className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-purple-200 hover:border-purple-300 transition-all duration-300 hover:shadow-lg">
            <div className="text-3xl font-bold text-purple-600 mb-2">0</div>
            <div className="text-purple-700 text-sm font-medium">Carbon Footprint</div>
          </div>
          <div className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-blue-200 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
            <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
            <div className="text-blue-700 text-sm font-medium">Fresh Preparation</div>
          </div>
        </div>
      </div>
    </section>
  );
}
